"use client";
import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import {
  getProfessionalDetails,
  deleteProfessionalContact,
  getProfessionalContactById,
} from "../../../api/services/Client";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import EditProfessionalContactModal from "./editProfessionalContactModal";
import DataTable from "../../Table/DataTable";

const ProfessionalContactTable = () => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [modalData, setModalData] = useState(null);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError, error } = useQuery(
    [QK.professionalContacts],
    getProfessionalDetails
  );

  const deleteMutation = useMutation(deleteProfessionalContact, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.professionalContacts]);
      enqueueSnackbar("Professional Contact deleted successfully", { variant: "success" });
    },
    onError: () => enqueueSnackbar("Failed to delete contact", { variant: "error" }),
  });

  const handleEdit = async (contactId) => {
    try {
      const response = await getProfessionalContactById(contactId);
      setModalData(response);
      setShowModal(true);
    } catch (error) {
      enqueueSnackbar("Failed to fetch contact details", { variant: "error" });
    }
  };

  const handleDeleteClick = (contactId) => {
    setSelectedContactId(contactId);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setShowConfirmModal(false);
    setSelectedContactId(null);
    setModalData(null);
  };

  const handleConfirmDelete = () => {
    if (selectedContactId) deleteMutation.mutate(selectedContactId);
    handleClose();
  };

  const headers = [
    { key: "name", label: "Name" },
    { key: "phone", label: "Phone" },
    { key: "otherPhone", label: "Other Phone" },
    { key: "email", label: "Email" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "name") {
      return (
        <Typography variant="body2" fontWeight={600} color="#0F172A">
          {item.first_name} {item.last_name}
        </Typography>
      );
    }
    if (header.key === "phone") {
      return (
        <Typography variant="body2" color="#475569">
          {item.mobile || "N/A"}
        </Typography>
      );
    }
    if (header.key === "otherPhone") {
      return (
        <Typography variant="body2" color="#475569">
          {item.telephone || "N/A"}
        </Typography>
      );
    }
    if (header.key === "email") {
      return (
        <Typography variant="body2" color="#475569">
          {item.email || "N/A"}
        </Typography>
      );
    }
    return item[header.key] || "-";
  };

  const customActions = (item) => (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      <Tooltip title="Edit" arrow>
        <IconButton
          size="small"
          onClick={() => handleEdit(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#8B5CF6",
              bgcolor: alpha("#8B5CF6", 0.06),
            },
          }}
        >
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => handleDeleteClick(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error" sx={{ my: 2 }}>Error: {error.message}</Alert>;
  }

  const contacts = data?.results || [];

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} color="#0F172A">
          Professional Contacts
        </Typography>
      </Box>

      <DataTable
        data={contacts}
        headers={headers}
        renderCell={renderCell}
        customActions={customActions}
        loading={isLoading}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleClose}
        handleConfirm={handleConfirmDelete}
        title="Confirm Delete"
        body="Are you sure you want to delete this contact?"
      />

      {showModal && modalData && (
        <EditProfessionalContactModal
          show={showModal}
          onHide={handleClose}
          data={modalData}
        />
      )}
    </Box>
  );
};

export default ProfessionalContactTable;